import {
  BellIcon,
  IconSizes,
  KeyIcon,
  MailIcon,
  MessageSquareIcon,
  Phone2Icon,
  ShieldIcon,
  type NewcoreIcon,
} from '@newcore/icons';
import { FontWeights } from '@newcore/ui/enums/font-weights';
import { TextSizes } from '@newcore/ui/enums/typography-text-sizes';
import { Text } from '@newcore/ui/typography';
import { FactorType } from '@/api/generated';
import { LoginCard } from '@/components/login-card';
import { Page } from '@/components/ui/page';
import type { AuthenticatorMetadata } from '@/hooks/use-mfa-orchestrator.types';

const FACTOR_ICONS: Partial<Record<FactorType, NewcoreIcon>> = {
  [FactorType.TOTP]: Phone2Icon,
  [FactorType.SMS_OTP]: MessageSquareIcon,
  [FactorType.EMAIL_OTP]: MailIcon,
  [FactorType.PUSH_NOTIFICATION]: BellIcon,
  [FactorType.WEBAUTHN]: ShieldIcon,
  [FactorType.WHATSAPP_OTP]: Phone2Icon,
  [FactorType.PASSWORD]: KeyIcon,
};

const DEFAULT_ICON = ShieldIcon;

interface AlternativesCardProps {
  alternatives: AuthenticatorMetadata[];
  onSelectAlternative: (factorType: string) => void;
  isLoading?: boolean;
  isEscalationFlow?: boolean;
}

/**
 * Card component that displays authentication methods
 * Backend returns all available options including the current factor
 */
export function AlternativesCard({
  alternatives,
  onSelectAlternative,
  isLoading = false,
  isEscalationFlow,
}: AlternativesCardProps) {
  return (
    <Page className='flex flex-col justify-center items-center'>
      <LoginCard className='h-full' isEscalationFlow={isEscalationFlow}>
        <div className='flex flex-col gap-3'>
          <div className='flex flex-col gap-0.5'>
            <Text
              as='h2'
              size={TextSizes.MD}
              weight={FontWeights.SEMIBOLD}
              className='text-primary'
            >
              Choose Authentication Method
            </Text>
            <Text size={TextSizes.XS} className='text-secondary'>
              Select an alternative way to authenticate
            </Text>
          </div>

          {/* Full list - no max height, grows to fit all options */}
          <div
            className='flex flex-col gap-1.5'
            data-testid='auth-method-chooser'
          >
            {alternatives.map(alt => {
              const Icon =
                FACTOR_ICONS[alt.factorType as FactorType] ?? DEFAULT_ICON;

              return (
                <button
                  key={alt.factorType}
                  onClick={() => onSelectAlternative(alt.factorType)}
                  disabled={isLoading}
                  className='flex items-center gap-2.5 py-2 px-3 rounded-md border border-secondary bg-primary hover:bg-secondary hover:border-utility-gray-400 transition-all duration-150 text-left disabled:opacity-50 disabled:cursor-not-allowed group'
                >
                  {/* Icon */}
                  <div className='flex-shrink-0 w-7 h-7 flex items-center justify-center rounded-md bg-secondary text-primary group-hover:bg-tertiary transition-colors'>
                    <Icon size={IconSizes.SM} strokeWidth={2} />
                  </div>

                  {/* Text content */}
                  <div className='flex flex-col min-w-0 flex-1'>
                    <Text
                      size={TextSizes.SM}
                      weight={FontWeights.MEDIUM}
                      className='text-primary leading-snug'
                    >
                      {alt.displayName}
                    </Text>
                    {alt.description && (
                      <Text
                        size={TextSizes.XS}
                        className='text-secondary line-clamp-1 leading-tight mt-0.5'
                      >
                        {alt.description}
                      </Text>
                    )}
                  </div>
                </button>
              );
            })}
          </div>
        </div>
      </LoginCard>
    </Page>
  );
}
